import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';

const EmailSchema = z.object({
  email: z.string().email({ message: 'Invalid email address' }),
});

export function useLogin(returnTo?: string) {
  const navigate = useNavigate();
  const { requestOTP, loading: authLoading, session, resendOTP, verifyOtp: authVerifyOtp } = useAuth();

  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [formErrors, setFormErrors] = useState<z.ZodFormattedError<typeof EmailSchema._input> | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError(null);
    setFormErrors(null);
    setResendSuccess(false);

    // Validate form data
    const validationResult = EmailSchema.safeParse({ email });

    if (!validationResult.success) {
      // Set formatted Zod errors to display in the UI
      setFormErrors(validationResult.error.format());
      return;
    }

    try {
      // Request OTP to be sent to email
      const { error } = await requestOTP(validationResult.data.email);
      if (error) {
        setApiError(error.message);
        return;
      }
      
      // Show OTP input
      setShowOtpInput(true);
    } catch (err: unknown) {
      let displayMessage = 'Failed to send verification code.';
      if (typeof err === 'object' && err !== null && 'message' in err && typeof err.message === 'string') {
        displayMessage = err.message;
      } else if (err instanceof Error) {
        displayMessage = err.message;
      }
      setApiError(displayMessage);
      console.error('Request OTP failed:', err);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setApiError('Please enter your email address');
      return;
    }

    try {
      setResendLoading(true);
      setResendSuccess(false);
      const { error } = await resendOTP(email);
      if (error) {
        setApiError(error.message);
        return;
      }
      setResendSuccess(true);
      setApiError(null);
    } catch (err: unknown) {
      let displayMessage = 'Failed to resend verification code.';
      if (typeof err === 'object' && err !== null && 'message' in err && typeof err.message === 'string') {
        displayMessage = err.message;
      } else if (err instanceof Error) {
        displayMessage = err.message;
      }
      setApiError(displayMessage);
      console.error('Resend OTP failed:', err);
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpVerification = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError(null);

    if (!otpCode.trim()) {
      setApiError('Please enter the verification code.');
      return;
    }

    try {
      const { error: verifyError, user } = await authVerifyOtp(email, otpCode);
      if (!verifyError) {
        if (user) {
          if (returnTo) {
            window.location.href = returnTo;
          } else {
            navigate({ to: '/' });
          }
        } else {
          setApiError('Verification failed. Please try again.');
        }
      } else {
        setApiError(verifyError.message);
      }
    } catch (err: unknown) {
      let displayMessage = 'Verification failed.';
      if (typeof err === 'object' && err !== null && 'message' in err && typeof err.message === 'string') {
        displayMessage = err.message;
      } else if (err instanceof Error) {
        displayMessage = err.message;
      }
      setApiError(displayMessage);
      console.error('OTP verification failed:', err);
    }
  };


  return {
    email,
    setEmail,
    otpCode,
    setOtpCode,
    formErrors,
    apiError,
    loading: authLoading,
    handleLogin,
    session,
    showOtpInput,
    resendLoading,
    resendSuccess,
    handleResendVerification,
    handleOtpVerification,
  };
}

export type UseLoginReturn = ReturnType<typeof useLogin>; 